import { Button, Spin } from "antd";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  getDocument,
  GlobalWorkerOptions,
  type PDFDocumentLoadingTask,
  type PDFDocumentProxy,
  type RenderTask
} from "pdfjs-dist";
import styles from "./PdfPreview.module.scss";

const PDF_WORKER_SRC = new URL(
  "pdfjs-dist/build/pdf.worker.min.js",
  import.meta.url
).toString();
const FIT_TO_WIDTH_PADDING_PX = 48;
const MIN_ACTUAL_SCALE = 0.1;
const MIN_ZOOM_RATIO_FLOOR = 0.05;
const MAX_ZOOM_RATIO = 2.5;
const ZOOM_STEP = 0.25;
const THUMBNAIL_TARGET_WIDTH_PX = 112;
const MIN_FIT_SCALE = 0.1;

if (
  typeof window !== "undefined" &&
  GlobalWorkerOptions.workerSrc !== PDF_WORKER_SRC
) {
  GlobalWorkerOptions.workerSrc = PDF_WORKER_SRC;
}

type PdfPreviewProps = {
  blob: Blob;
  onDownload?: () => void;
  toolbarContainer?: HTMLElement | null;
  renderToolbarInHeader?: boolean;
};

type PdfPageCanvasProps = {
  pdfDocument: PDFDocumentProxy;
  pageNumber: number;
  scale: number;
};

type PdfPageThumbnailProps = {
  pdfDocument: PDFDocumentProxy;
  pageNumber: number;
  isActive: boolean;
  onSelect: (pageNumber: number) => void;
  registerThumbnailRef: (pageNumber: number, element: HTMLButtonElement | null) => void;
};

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function PdfPageCanvas({
  pdfDocument,
  pageNumber,
  scale
}: PdfPageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isRendering, setIsRendering] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let isActive = true;
    let renderTask: RenderTask | null = null;

    const renderPage = async () => {
      setIsRendering(true);
      setError(null);

      try {
        const page = await pdfDocument.getPage(pageNumber);
        if (!isActive) return;

        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const context = canvas?.getContext("2d", { alpha: false });

        if (!canvas || !context) {
          throw new Error("Canvas is not available.");
        }

        const outputScale = window.devicePixelRatio || 1;

        canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
        canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        renderTask = page.render({
          canvasContext: context,
          viewport,
          transform:
            outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0]
        });

        await renderTask.promise;
        if (!isActive) return;

        setIsRendering(false);
      } catch (nextError) {
        if (!isActive) return;
        if ((nextError as { name?: string })?.name === "RenderingCancelledException") {
          return;
        }

        setIsRendering(false);
        setError("Không thể hiển thị trang PDF này.");
      }
    };

    renderPage();

    return () => {
      isActive = false;
      renderTask?.cancel();
    };
  }, [pageNumber, pdfDocument, scale]);

  return (
    <div className={styles.page}>
      <div className={styles.pageLabel}>Trang {pageNumber}</div>
      <div className={styles.canvasShell}>
        {isRendering && !error ? (
          <div className={styles.pageOverlay}>
            <Spin />
          </div>
        ) : null}
        {error ? <div className={styles.pageError}>{error}</div> : null}
        <canvas
          ref={canvasRef}
          className={isRendering || error ? styles.canvasHidden : styles.canvas}
        />
      </div>
    </div>
  );
}

function PdfPageThumbnail({
  pdfDocument,
  pageNumber,
  isActive,
  onSelect,
  registerThumbnailRef
}: PdfPageThumbnailProps) {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [isRendering, setIsRendering] = useState(true);

  useEffect(() => {
    let isActiveRender = true;
    let renderTask: RenderTask | null = null;

    const renderThumbnail = async () => {
      setIsRendering(true);

      try {
        const page = await pdfDocument.getPage(pageNumber);
        if (!isActiveRender) return;

        const baseViewport = page.getViewport({ scale: 1 });
        const scale = THUMBNAIL_TARGET_WIDTH_PX / baseViewport.width;
        const viewport = page.getViewport({ scale });
        const canvas = canvasRef.current;
        const context = canvas?.getContext("2d", { alpha: false });

        if (!canvas || !context) {
          throw new Error("Canvas is not available.");
        }

        const outputScale = window.devicePixelRatio || 1;

        canvas.width = Math.max(1, Math.floor(viewport.width * outputScale));
        canvas.height = Math.max(1, Math.floor(viewport.height * outputScale));
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        renderTask = page.render({
          canvasContext: context,
          viewport,
          transform:
            outputScale === 1 ? undefined : [outputScale, 0, 0, outputScale, 0, 0]
        });

        await renderTask.promise;
        if (!isActiveRender) return;

        setIsRendering(false);
      } catch (_error) {
        if (!isActiveRender) return;
        setIsRendering(false);
      }
    };

    renderThumbnail();

    return () => {
      isActiveRender = false;
      renderTask?.cancel();
    };
  }, [pageNumber, pdfDocument]);

  return (
    <button
      ref={(element) => registerThumbnailRef(pageNumber, element)}
      type="button"
      className={isActive ? styles.thumbnailButtonActive : styles.thumbnailButton}
      onClick={() => onSelect(pageNumber)}
      aria-label={`Chuyển đến trang ${pageNumber}`}
    >
      <span className={styles.thumbnailIndex}>Trang {pageNumber}</span>
      <span className={styles.thumbnailFrame}>
        {isRendering ? (
          <span className={styles.thumbnailOverlay}>
            <Spin size="small" />
          </span>
        ) : null}
        <canvas ref={canvasRef} className={styles.thumbnailCanvas} />
      </span>
    </button>
  );
}

function PdfPreview({
  blob,
  onDownload,
  toolbarContainer,
  renderToolbarInHeader = false
}: PdfPreviewProps) {
  const viewportRef = useRef<HTMLDivElement | null>(null);
  const sidebarRef = useRef<HTMLDivElement | null>(null);
  const pageRefs = useRef<Record<number, HTMLDivElement | null>>({});
  const thumbnailRefs = useRef<Record<number, HTMLButtonElement | null>>({});
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [basePageWidth, setBasePageWidth] = useState<number | null>(null);
  const [zoomRatio, setZoomRatio] = useState(1);
  const [zoomInputValue, setZoomInputValue] = useState("100");
  const [isEditingZoom, setIsEditingZoom] = useState(false);
  const [activePageNumber, setActivePageNumber] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const element = viewportRef.current;
    if (!element) return;

    const updateWidth = () => {
      setContainerWidth(element.clientWidth);
    };

    updateWidth();

    if (typeof ResizeObserver === "undefined") {
      window.addEventListener("resize", updateWidth);
      return () => {
        window.removeEventListener("resize", updateWidth);
      };
    }

    const observer = new ResizeObserver(updateWidth);
    observer.observe(element);

    return () => {
      observer.disconnect();
    };
  }, []);

  useEffect(() => {
    let isActive = true;
    let loadingTask: PDFDocumentLoadingTask | null = null;

    const loadDocument = async () => {
      setPdfDocument(null);
      setBasePageWidth(null);
      setZoomRatio(1);
      setIsLoading(true);
      setError(null);

      try {
        const data = new Uint8Array(await blob.arrayBuffer());
        if (!isActive) return;

        loadingTask = getDocument({
          data,
          useWorkerFetch: false
        });

        const nextDocument = await loadingTask.promise;
        if (!isActive) {
          await loadingTask.destroy();
          return;
        }

        const firstPage = await nextDocument.getPage(1);
        if (!isActive) {
          await loadingTask.destroy();
          return;
        }

        setBasePageWidth(firstPage.getViewport({ scale: 1 }).width);
        setActivePageNumber(1);
        setPdfDocument(nextDocument);
      } catch (_error) {
        if (!isActive) return;
        setError("Không thể hiển thị bản xem trước PDF.");
      } finally {
        if (isActive) {
          setIsLoading(false);
        }
      }
    };

    loadDocument();

    return () => {
      isActive = false;
      if (loadingTask) {
        void loadingTask.destroy();
      }
    };
  }, [blob]);

  const fitScale = useMemo(() => {
    if (!basePageWidth || !containerWidth) return 1;

    const availableWidth = Math.max(containerWidth - FIT_TO_WIDTH_PADDING_PX, 1);

    return Math.max(availableWidth / basePageWidth, MIN_FIT_SCALE);
  }, [basePageWidth, containerWidth]);

  const minZoomRatio = useMemo(() => {
    return clamp(
      MIN_ACTUAL_SCALE / fitScale,
      MIN_ZOOM_RATIO_FLOOR,
      MAX_ZOOM_RATIO
    );
  }, [fitScale]);

  const scale = fitScale * zoomRatio;
  const zoomPercentage = Math.round(scale * 100);
  const minZoomPercentage = Math.round(MIN_ACTUAL_SCALE * 100);
  const maxZoomPercentage = Math.max(
    minZoomPercentage,
    Math.round(fitScale * MAX_ZOOM_RATIO * 100)
  );
  const pageNumbers = useMemo(() => {
    if (!pdfDocument) return [];
    return Array.from({ length: pdfDocument.numPages }, (_, index) => index + 1);
  }, [pdfDocument]);

  useEffect(() => {
    if (isEditingZoom) return;
    setZoomInputValue(String(zoomPercentage));
  }, [isEditingZoom, zoomPercentage]);

  useEffect(() => {
    const viewportElement = viewportRef.current;
    if (!viewportElement || pageNumbers.length === 0) return;

    const updateActivePage = () => {
      const viewportRect = viewportElement.getBoundingClientRect();
      const viewportCenter = viewportRect.top + viewportRect.height * 0.35;

      let nextActivePage = activePageNumber;
      let closestDistance = Number.POSITIVE_INFINITY;

      pageNumbers.forEach((pageNumber) => {
        const element = pageRefs.current[pageNumber];
        if (!element) return;

        const rect = element.getBoundingClientRect();
        const pageCenter = rect.top + rect.height / 2;
        const distance = Math.abs(pageCenter - viewportCenter);

        if (distance < closestDistance) {
          closestDistance = distance;
          nextActivePage = pageNumber;
        }
      });

      setActivePageNumber((currentPage) =>
        currentPage === nextActivePage ? currentPage : nextActivePage
      );
    };

    updateActivePage();
    viewportElement.addEventListener("scroll", updateActivePage, {
      passive: true
    });
    window.addEventListener("resize", updateActivePage);

    return () => {
      viewportElement.removeEventListener("scroll", updateActivePage);
      window.removeEventListener("resize", updateActivePage);
    };
  }, [activePageNumber, pageNumbers, scale]);

  useEffect(() => {
    const activeThumbnail = thumbnailRefs.current[activePageNumber];
    if (!activeThumbnail) return;

    activeThumbnail.scrollIntoView({
      block: "nearest",
      inline: "nearest"
    });
  }, [activePageNumber]);

  const handleSelectPage = (pageNumber: number) => {
    setActivePageNumber(pageNumber);
    pageRefs.current[pageNumber]?.scrollIntoView({
      behavior: "smooth",
      block: "start"
    });
  };

  const registerPageRef = (pageNumber: number, element: HTMLDivElement | null) => {
    pageRefs.current[pageNumber] = element;
  };

  const registerThumbnailRef = (
    pageNumber: number,
    element: HTMLButtonElement | null
  ) => {
    thumbnailRefs.current[pageNumber] = element;
  };

  const resetZoomInput = () => {
    setZoomInputValue(String(zoomPercentage));
    setIsEditingZoom(false);
  };

  const applyZoomPercentage = (percentage: number) => {
    const clampedPercentage = clamp(
      percentage,
      minZoomPercentage,
      maxZoomPercentage
    );
    const nextScale = clampedPercentage / 100;
    const nextZoomRatio = clamp(
      nextScale / fitScale,
      minZoomRatio,
      MAX_ZOOM_RATIO
    );

    setZoomRatio(nextZoomRatio);
    setZoomInputValue(String(Math.round(nextScale * 100)));
    setIsEditingZoom(false);
  };

  const applyZoomInput = () => {
    const normalizedValue = zoomInputValue.trim().replace("%", "");
    const parsedValue = Number(normalizedValue.replace(",", "."));

    if (!normalizedValue || Number.isNaN(parsedValue)) {
      resetZoomInput();
      return;
    }

    applyZoomPercentage(parsedValue);
  };

  const toolbarContent =
    pdfDocument && !isLoading && !error ? (
      <div
        className={
          renderToolbarInHeader
            ? `${styles.toolbar} ${styles.toolbarHeader}`
            : `${styles.toolbar} ${styles.toolbarInline}`
        }
      >
        <div className={styles.meta}>{pdfDocument.numPages} trang</div>
        <div className={styles.controls}>
          <Button
            size="small"
            onClick={() =>
              setZoomRatio((currentZoom) =>
                clamp(currentZoom - ZOOM_STEP, minZoomRatio, MAX_ZOOM_RATIO)
              )
            }
            disabled={zoomRatio <= minZoomRatio}
          >
            Thu nhỏ
          </Button>
          <Button size="small" onClick={() => setZoomRatio(1)}>
            Vừa khung
          </Button>
          <label className={styles.zoomInputShell}>
            <input
              type="text"
              inputMode="decimal"
              value={zoomInputValue}
              className={styles.zoomInput}
              aria-label="Phần trăm thu phóng PDF"
              onFocus={(event) => {
                setIsEditingZoom(true);
                event.currentTarget.select();
              }}
              onChange={(event) => {
                setZoomInputValue(event.target.value);
              }}
              onBlur={applyZoomInput}
              onKeyDown={(event) => {
                if (event.key === "ArrowUp" || event.key === "ArrowDown") {
                  event.preventDefault();
                  const delta = event.key === "ArrowUp" ? 1 : -1;
                  const normalizedValue = zoomInputValue.trim().replace("%", "");
                  const parsedValue = Number(normalizedValue.replace(",", "."));
                  const baseValue = Number.isNaN(parsedValue)
                    ? zoomPercentage
                    : parsedValue;
                  const nextValue = clamp(
                    baseValue + delta,
                    minZoomPercentage,
                    maxZoomPercentage
                  );

                  setZoomInputValue(String(nextValue));
                  applyZoomPercentage(nextValue);
                  return;
                }

                if (event.key === "Enter") {
                  applyZoomInput();
                  event.currentTarget.blur();
                }

                if (event.key === "Escape") {
                  resetZoomInput();
                  event.currentTarget.blur();
                }
              }}
            />
            <span className={styles.zoomSuffix}>%</span>
          </label>
          <Button
            size="small"
            onClick={() =>
              setZoomRatio((currentZoom) =>
                clamp(currentZoom + ZOOM_STEP, minZoomRatio, MAX_ZOOM_RATIO)
              )
            }
            disabled={zoomRatio >= MAX_ZOOM_RATIO}
          >
            Phóng to
          </Button>
        </div>
      </div>
    ) : null;

  if (isLoading) {
    return (
      <div className={styles.state}>
        <Spin size="large" />
        <span>Đang tải bản xem trước PDF...</span>
      </div>
    );
  }

  if (error || !pdfDocument) {
    return (
      <div className={styles.state}>
        <span>{error || "Không thể hiển thị bản xem trước PDF."}</span>
        {onDownload ? (
          <Button size="small" onClick={onDownload}>
            Tải xuống
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <div className={styles.root}>
      {toolbarContainer && toolbarContent
        ? createPortal(toolbarContent, toolbarContainer)
        : null}
      {!renderToolbarInHeader ? toolbarContent : null}
      <div className={styles.body}>
        <div ref={sidebarRef} className={styles.sidebar}>
          <div className={styles.sidebarTitle}>Trang</div>
          <div className={styles.thumbnailList}>
            {pageNumbers.map((pageNumber) => (
              <PdfPageThumbnail
                key={`thumbnail-${pageNumber}`}
                pdfDocument={pdfDocument}
                pageNumber={pageNumber}
                isActive={activePageNumber === pageNumber}
                onSelect={handleSelectPage}
                registerThumbnailRef={registerThumbnailRef}
              />
            ))}
          </div>
        </div>
        <div ref={viewportRef} className={styles.viewport}>
          <div className={styles.pages}>
          {pageNumbers.map((pageNumber) => (
              <div
                key={`${pageNumber}-${scale}`}
                ref={(element) => registerPageRef(pageNumber, element)}
                className={styles.pageAnchor}
              >
                <PdfPageCanvas
                  pdfDocument={pdfDocument}
                  pageNumber={pageNumber}
                  scale={scale}
                />
              </div>
          ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default PdfPreview;
